<template>
  <div v-if="pet">
    <div class="flex m-5 gap-5">
      <div class="flex flex-col p-5 bg-white rounded-lg">
        <div class="flex flex-col items-center gap-4">
          <div class="w-40 h-40">
            <img v-if="imageUrl" :src="imageUrl" alt="Pet Image" class="object-cover w-full h-full rounded-lg">
          </div>
          <Button
            class="bg-primary"
            :disabled="uploading"
            @click="($refs.fileInput as InstanceType<typeof Input>).$el.click()"
          >
            <ImageUp :class="uploading ? 'animate-spin' : ''" />
            Changer l'image
          </Button>
        </div>
        <Input
          ref="fileInput"
          type="file"
          accept="image/*"
          class="hidden"
          @change="(event) => upload((event.target as HTMLInputElement).files)"
        />
      </div>
      <div class="flex flex-1 flex-col gap-3 p-5 bg-white rounded-lg max-w-80 relative">
        <div class="absolute right-2">
          <AlertDialog>
            <DropdownMenu>
              <DropdownMenuTrigger as-child>
                <Icon name="mdi-dots-vertical" :size="20" color="gray" />
              </DropdownMenuTrigger>
              <DropdownMenuContent class="w-56" side="right">
                <DropdownMenuGroup>
                  <DropdownMenuItem class="text-red">
                    <AlertDialogTrigger class="flex gap-1">
                      <Trash2 :size="14" /> Supprimer {{ pet.name }}
                    </AlertDialogTrigger>
                  </DropdownMenuItem>
                </DropdownMenuGroup>
              </DropdownMenuContent>
            </DropdownMenu>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle class="font-bold">
                  Êtes-vous sûr ?
                </AlertDialogTitle>
                <AlertDialogDescription class="text-sm">
                  {{ pet.name }} sera supprimé. Cette action est irréversible.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter class="flex items-center">
                <AlertDialogCancel>Annuler</AlertDialogCancel>
                <AlertDialogAction class="bg-red" @click="_delete">
                  Supprimer {{ pet.name }}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
        <div>
          <Label for="name" class="font-bold">Nom de l'animal</Label>
          <Input id="name" v-model="name" class="w-64" />
        </div>
        <div>
          <Label for="category">Type de l'animal</Label>
          <Select id="category" v-model="selectedCategory">
            <SelectTrigger class="w-64">
              <SelectValue placeholder="Sélectionnez une catégorie" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem
                v-for="category in categories"
                :key="category.id"
                :value="category.id.toString()"
              >
                {{ category.name }}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div class="flex flex-col flex-1 justify-end">
          <Button class="bg-primary" :disabled="updating" @click="update">
            <Save :class="updating ? 'animate-spin' : ''" />
            Enregistrer
          </Button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>

import {Pet, PetCategory} from "@/customer/interfaces/Pet";
import Customer from "@/customer/interfaces/Customer";
import {onMounted, ref, watch} from "vue";
import {useRoute, useRouter} from "vue-router";
import {Input} from "@/core/components/ui/input";
import {petApi} from "@/container";
import {Label} from "@/core/components/ui/label";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "@/core/components/ui/select";
import defaultImage from '@/assets/images/pets.jpg';
import {Button} from "@/core/components/ui/button";
import {ImageUp, Save, Trash2} from "@lucide/vue";
import {usePatientStore} from "@/stores/patient.store";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuTrigger
} from "@/core/components/ui/dropdown-menu";
import Icon from "@/core/components/icon/Icon.vue";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTrigger
} from "@/core/components/ui/alert-dialog";

const route = useRoute();
const router = useRouter();
const pet = ref<Pet | undefined>(undefined);
const selectedCategory = ref<string>();
const name = ref<string>();
const categories = ref<PetCategory[]>([]);
const imageUrl = ref<string>();
const uploading = ref<boolean>(false);
const updating = ref<boolean>(false);
const {updatePet, deletePet} = usePatientStore();

const props = defineProps<{
    customer: Customer;
}>();

onMounted(async () => {
    categories.value = await petApi().categories();
});

/**
 * Load the image for the pet
 */
const fetchImage = async (pet: Pet, cache: boolean = true): Promise<string> => {
    if (!pet.image) {
        return defaultImage;
    }

    const blob = await petApi().image(pet.id, cache);

    return URL.createObjectURL(blob);
};

/**
 * Upload an image for the pet
 *
 * @param files The files to upload
 */
const upload = async (files: FileList | null) => {
    await petApi().upload(pet.value!.id, files);

    uploading.value = true;

    const updatedPet = await petApi().read(pet.value!.id);

    imageUrl.value = await fetchImage(updatedPet, false);

    uploading.value = false;
};

/**
 * Update the pet with the current values
 */
const update = async () => {
    updating.value = true;

    const updatedPet = await petApi().update(pet.value!.id, {
        name: name.value,
        category_id: Number(selectedCategory.value)
    });

    await updatePet(updatedPet);

    updating.value = false;
};

/** Delete the pet. */
const _delete = async () => {
    await petApi().delete(pet.value!.id).catch();
    await deletePet(pet.value!);
    await router.push({name: 'customer.home', params: {customerId: props.customer.id}});
};

watch(() => route.params.id, async (id) => {
    pet.value = props.customer.pets.find((pet: Pet) => pet.id === Number(id));
    name.value = pet.value?.name;
    selectedCategory.value = pet.value?.category.id.toString();
    imageUrl.value = await fetchImage(pet.value!);
}, {immediate: true});

</script>
